import { useMemo, useState } from 'react';
import { ConfidenceIcon } from './ConfidenceIcon';
import type { FieldConfidence } from '../types/extraction';

/**
 * PROD1.7.8 N3 — Panneau pliable listant la confiance par champ.
 *
 * Affiché sous le badge de score global dans la page extraction. Permet à
 * l'analyste de voir en un coup d'œil les champs incertains AVANT d'attaquer
 * le formulaire, sans devoir injecter une icône dans DynamicForm (rendu
 * schéma-driven, refactor au-delà du scope S3).
 *
 * Trie par confidence ASC (low en premier — ce sont ceux qu'il faut regarder)
 * puis par nom de champ alphabétique.
 *
 * No-op (renvoie null) si `fieldConfidences` est vide ou undefined (extraction
 * legacy sans Prompt 1.3).
 */
interface FieldConfidencePanelProps {
  fieldConfidences?: Record<string, FieldConfidence>;
}

const ORDER: Record<FieldConfidence['confidence'], number> = {
  low: 0,
  medium: 1,
  high: 2,
};

export function FieldConfidencePanel({ fieldConfidences }: FieldConfidencePanelProps) {
  const [open, setOpen] = useState(false);

  const entries = useMemo(() => {
    if (!fieldConfidences) return [];
    return Object.entries(fieldConfidences).sort((a, b) => {
      const da = ORDER[a[1].confidence] ?? 99;
      const db = ORDER[b[1].confidence] ?? 99;
      if (da !== db) return da - db;
      return a[0].localeCompare(b[0]);
    });
  }, [fieldConfidences]);

  const counts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 };
    for (const [, v] of entries) c[v.confidence]++;
    return c;
  }, [entries]);

  if (entries.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900"
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>Confiance par champ :</span>
        <span className="text-emerald-700">🟢 {counts.high}</span>
        <span className="text-amber-700">🟡 {counts.medium}</span>
        <span className="text-red-700">🔴 {counts.low}</span>
      </button>
      {open && (
        <div className="mt-2 ml-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-xs">
          {entries.map(([field, meta]) => (
            <div key={field} className="flex items-center gap-2">
              <ConfidenceIcon level={meta.confidence} reason={meta.reason} size="small" />
              <span className="font-mono text-gray-700">{field}</span>
              {meta.reason && meta.confidence !== 'high' && (
                <span className="text-gray-500 truncate" title={meta.reason}>
                  — {meta.reason}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
